import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiRequest } from "../../api/client";

export const signup = createAsyncThunk(
  "auth/signup",
  async ({ full_name, email, password, password_confirmation }, thunkApi) => {
    const res = await apiRequest("api/signup", {
      method: "POST",
      body: { full_name, email, password, password_confirmation },
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") ||
          res.data?.message ||
          "Signup failed. Please check your details."
      );
    }

    return res.data;
  }
);

export const login = createAsyncThunk(
  "auth/login",
  async ({ email, password }, thunkApi) => {
    const res = await apiRequest("api/login", {
      method: "POST",
      body: { email, password },
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") ||
          res.data?.message ||
          "Login failed. Please check your credentials."
      );
    }

    return res.data;
  }
);

export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async ({ otp }, thunkApi) => {
    const res = await apiRequest("api/verify_otp", {
      method: "POST",
      body: { otp },
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") ||
          res.data?.message ||
          "OTP verification failed. Please try again."
      );
    }

    return res.data;
  }
);

export const resendOtp = createAsyncThunk(
  "auth/resendOtp",
  async (_, thunkApi) => {
    const res = await apiRequest("api/resend_otp", {
      method: "POST",
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") ||
          res.data?.message ||
          "Failed to resend OTP. Please try again."
      );
    }

    return res.data;
  }
);

export const fetchCurrentUser = createAsyncThunk(
  "auth/fetchCurrentUser",
  async (arg, thunkApi) => {
    const res = await apiRequest("api/me", {
      method: "GET",
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") ||
          res.data?.message ||
          "Failed to fetch profile."
      );
    }

    return res.data;
  },
  {
    // Prevent duplicate /api/me calls (e.g. React StrictMode remounts).
    condition: (arg, { getState }) => {
      const state = getState();
      const auth = state?.auth;
      if (!auth) return true;
      if (arg?.force) return true;
      if (auth.user) return false; // already have user
      if (auth.status === "loading") return false; // request in flight
      return true;
    },
  }
);

export const updateProfile = createAsyncThunk(
  "auth/updateProfile",
  async (
    {
      full_name,
      address_line1,
    },
    thunkApi
  ) => {
    const res = await apiRequest("api/profile", {
      method: "PUT",
      body: {
        full_name: full_name || undefined,
        address_line1: address_line1 ?? null,
      },
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") ||
          res.data?.message ||
          "Failed to update profile."
      );
    }

    return res.data;
  }
);

const initialState = {
  token: (() => {
    try {
      return localStorage.getItem("femtyz_token");
    } catch {
      return null;
    }
  })(),
  status: "idle", // idle | loading | succeeded | failed
  error: "",
  user: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout(state) {
      state.token = null;
      try {
        localStorage.removeItem("femtyz_token");
      } catch {
        // ignore
      }
    },
    setToken(state, action) {
      state.token = action.payload || null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signup.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = "";
        const token = action.payload?.token;
        if (token) {
          state.token = token;
          try {
            localStorage.setItem("femtyz_token", token);
          } catch {
            // ignore
          }
        }
      })
      .addCase(signup.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Signup failed.";
      })
      .addCase(login.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = "";
        const token = action.payload?.token;
        if (token) {
          state.token = token;
          try {
            localStorage.setItem("femtyz_token", token);
          } catch {
            // ignore
          }
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Login failed.";
      })
      .addCase(verifyOtp.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(verifyOtp.fulfilled, (state) => {
        state.status = "succeeded";
        state.error = "";
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "OTP verification failed.";
      })
      .addCase(resendOtp.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(resendOtp.fulfilled, (state) => {
        state.status = "succeeded";
        state.error = "";
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to resend OTP.";
      })
      .addCase(fetchCurrentUser.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.user = action.payload || null;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to fetch profile.";
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.user = action.payload || state.user;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to update profile.";
      });
  },
});

export const { logout, setToken } = authSlice.actions;
export default authSlice.reducer;

